import { useCallback, useEffect, useState } from 'react';
import { fetchApiRef, useApi } from '@backstage/frontend-plugin-api';
import { apiError } from '@internal/ui';

const WORKER_URL = '/sw.js';
const API = 'plugin://platform-orchestrator';
/** Set when the user turned incident pushes off on this browser: they no longer turn on by themselves. */
export const PUSH_OFF_KEY = 'bobbee.incidentPush.off';

/** Pushes are on by default; only an explicit « Désactiver » on this browser keeps them off. */
export function pushTurnedOff(): boolean {
  try {
    return window.localStorage.getItem(PUSH_OFF_KEY) === '1';
  } catch {
    return false;
  }
}

function setPushTurnedOff(off: boolean) {
  try {
    if (off) window.localStorage.setItem(PUSH_OFF_KEY, '1');
    else window.localStorage.removeItem(PUSH_OFF_KEY);
  } catch {
    // Private window: the choice lasts as long as the subscription.
  }
}

/**
 * `on`/`off`: this browser receives incident pushes or not. `denied`: notifications are blocked
 * for the site. `unsupported`: no Push API (insecure origin, old browser). `unavailable`: Web
 * Push is off on the portal.
 */
export type PushState =
  | 'loading'
  | 'on'
  | 'off'
  | 'denied'
  | 'unsupported'
  | 'unavailable';

/** VAPID public key (base64url) → the bytes `pushManager.subscribe` expects. */
export function keyBytes(base64Url: string): Uint8Array {
  const base64 = (base64Url + '='.repeat((4 - (base64Url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
}

const supported = () =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'PushManager' in window &&
  Boolean(navigator.serviceWorker);

/**
 * Web Push of incidents for this browser (docs/incidents.md#push-notifications): they show even
 * with no portal tab open. The subscription belongs to the browser profile; the backend keeps it
 * with the signed-in user. On by default: as soon as notifications are allowed for the site (the
 * portal asks on the first click), the browser subscribes, unless the user turned pushes off here.
 */
export function useIncidentPush() {
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<PushState>('loading');
  const [publicKey, setPublicKey] = useState<string>();
  const [error, setError] = useState<string>();

  const save = useCallback(
    async (subscription: PushSubscription) => {
      const res = await fetchApi.fetch(`${API}/me/web-push`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription.toJSON()),
      });
      if (!res.ok) throw await apiError(res);
    },
    [fetchApi],
  );

  /** Subscribes without asking: the permission is already granted. */
  const subscribe = useCallback(
    async (key: string) => {
      const registration = await navigator.serviceWorker.register(WORKER_URL);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(key),
      });
      await save(subscription);
      setState('on');
    },
    [save],
  );

  useEffect(() => {
    if (!supported()) {
      setState('unsupported');
      return;
    }
    (async () => {
      const res = await fetchApi.fetch(`${API}/web-push`);
      const key = res.ok
        ? ((await res.json()) as { publicKey: string | null }).publicKey
        : null;
      if (!key) {
        setState('unavailable');
        return;
      }
      setPublicKey(key);
      if (Notification.permission === 'denied') {
        setState('denied');
        return;
      }
      const registration = await navigator.serviceWorker.register(WORKER_URL);
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // Sent again on each visit: the portal may have lost it (database reset, other user).
        await save(subscription).catch(() => undefined);
        setState('on');
      } else if (Notification.permission === 'granted' && !pushTurnedOff()) {
        await subscribe(key);
      } else {
        setState('off');
      }
    })().catch(() => setState('unavailable'));
  }, [fetchApi, save, subscribe]);

  // Allowed later (the portal's first-click prompt, the site settings): subscribe then.
  useEffect(() => {
    if (state !== 'off' || !publicKey || !navigator.permissions)
      return undefined;
    let status: PermissionStatus | undefined;
    const onChange = () => {
      if (status?.state === 'granted' && !pushTurnedOff()) {
        subscribe(publicKey).catch(e => setError((e as Error).message));
      } else if (status?.state === 'denied') {
        setState('denied');
      }
    };
    navigator.permissions
      .query({ name: 'notifications' })
      .then(s => {
        status = s;
        s.addEventListener('change', onChange);
      })
      .catch(() => undefined);
    return () => status?.removeEventListener('change', onChange);
  }, [state, publicKey, subscribe]);

  const enable = useCallback(async () => {
    if (!publicKey) return;
    setError(undefined);
    try {
      setPushTurnedOff(false);
      if ((await Notification.requestPermission()) !== 'granted') {
        setState('denied');
        return;
      }
      await subscribe(publicKey);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [publicKey, subscribe]);

  const disable = useCallback(async () => {
    setError(undefined);
    setPushTurnedOff(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration(
        WORKER_URL,
      );
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await fetchApi.fetch(`${API}/me/web-push`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
        await subscription.unsubscribe();
      }
      setState('off');
    } catch (e) {
      setError((e as Error).message);
    }
  }, [fetchApi]);

  return { state, error, enable, disable };
}
